/**
 * Delt rutenett-geometri for områdebasert caching.
 *
 * 0,05° ≈ 5 km celler — samme oppløsning som `spotCacheService` bruker, slik at
 * frekvens-sporing (regionUsageService), spot-cache og tile-vekting refererer til
 * NØYAKTIG samme celler. Endre GRID_SIZE her og alt følger.
 */
import type { MapBounds } from '../types';

export const GRID_SIZE = 0.05; // grader (~5 km ved våre breddegrader)

/** Cellenøkkel for et punkt. */
export const gridKey = (lat: number, lon: number): string =>
  `${Math.floor(lat / GRID_SIZE)}_${Math.floor(lon / GRID_SIZE)}`;

/** Alle cellenøkler som et bounds dekker (inklusiv kanter). */
export const gridKeysForBounds = (b: MapBounds): string[] => {
  const keys: string[] = [];
  const minRow = Math.floor(b.latMin / GRID_SIZE);
  const maxRow = Math.floor(b.latMax / GRID_SIZE);
  const minCol = Math.floor(b.lonMin / GRID_SIZE);
  const maxCol = Math.floor(b.lonMax / GRID_SIZE);
  for (let r = minRow; r <= maxRow; r++) {
    for (let c = minCol; c <= maxCol; c++) keys.push(`${r}_${c}`);
  }
  return keys;
};

/** Cellenøkkel for senter av et bounds. */
export const gridKeyForBoundsCenter = (b: MapBounds): string =>
  gridKey((b.latMin + b.latMax) / 2, (b.lonMin + b.lonMax) / 2);

/**
 * Senter-lat/lon for en XYZ-flis. Web-Mercator invers. Brukes til å mappe en
 * Leaflet-flis (som vi kjenner x/y/z for i createTile) til frekvens-cellen sin —
 * uten å parse tjener-spesifikke URL-format.
 */
export const tileCenterLatLon = (x: number, y: number, z: number): { lat: number; lon: number } => {
  const n = Math.pow(2, z);
  const lon = ((x + 0.5) / n) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 0.5)) / n)));
  return { lat: (latRad * 180) / Math.PI, lon };
};

/** Cellenøkkel for en XYZ-flis. */
export const gridKeyForTile = (x: number, y: number, z: number): string => {
  const { lat, lon } = tileCenterLatLon(x, y, z);
  return gridKey(lat, lon);
};

/**
 * Invers av gridKey: cellenøkkel → dens lat/lon-boks. Brukes til å varme opp
 * («prefetche») en hyppig brukt celle. Returnerer null for ugyldig nøkkel.
 */
export const cellKeyToBounds = (cellKey: string): MapBounds | null => {
  const parts = cellKey.split('_');
  if (parts.length !== 2) return null;
  const row = Number(parts[0]);
  const col = Number(parts[1]);
  if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
  return {
    latMin: row * GRID_SIZE,
    latMax: (row + 1) * GRID_SIZE,
    lonMin: col * GRID_SIZE,
    lonMax: (col + 1) * GRID_SIZE,
  };
};
